import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    NativeSelect,
    FormHelperText,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import PreviewIcon from '@mui/icons-material/Preview';
import DOMPurify from 'dompurify';
import {
    GetTemplatesForModule,
    PreviewCommunicationTemplate,
    SendCommunicationTemplate,
} from '../../REST/communicationTemplates';

/**
 * SendTemplateDialog
 *
 * Opens when staff wants to manually send a communication template to a
 * specific module record (e.g., a Waitlist applicant, Invoice, Agreement, etc.).
 *
 * Props:
 *   open        - boolean
 *   onClose     - () => void
 *   module      - string  — module key matching the backend (e.g. 'Waitlist', 'Invoices')
 *   entityId    - string  — _id of the record to use for variable substitution
 *   defaultEmail - string — pre-filled recipient (from entity.email)
 */
export default function SendTemplateDialog({ open, onClose, module, entityId, defaultEmail = '' }) {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [recipientEmail, setRecipientEmail] = useState(defaultEmail);
    const [preview, setPreview] = useState(null);   // { subject, html, templateName }
    const [step, setStep] = useState('pick');        // 'pick' | 'preview'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(false);

    // Reset state whenever dialog opens
    useEffect(() => {
        if (open) {
            setSelectedTemplateId('');
            setRecipientEmail(defaultEmail);
            setPreview(null);
            setStep('pick');
            setError(null);
            setSent(false);
            loadTemplates();
        }
    }, [open, module, defaultEmail]);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const res = await GetTemplatesForModule(module);
            setTemplates(res.data || []);
        } catch (ex) {
            setError('Failed to load templates.');
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        if (!selectedTemplateId) { setError('Please select a template.'); return; }
        setError(null);
        setLoading(true);
        try {
            const res = await PreviewCommunicationTemplate(selectedTemplateId, module, entityId);
            const data = res.data;
            setPreview(data);
            // Pre-fill recipient from entity if not already set
            if (!recipientEmail && data.recipientEmail) {
                setRecipientEmail(data.recipientEmail);
            }
            setStep('preview');
        } catch (ex) {
            setError(ex?.response?.data?.message || 'Failed to load preview.');
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!recipientEmail) { setError('Recipient email is required.'); return; }
        setError(null);
        setLoading(true);
        try {
            await SendCommunicationTemplate(selectedTemplateId, module, entityId, recipientEmail);
            setSent(true);
        } catch (ex) {
            setError(ex?.response?.data?.message || 'Failed to send email.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" />
                Send Communication Email
            </DialogTitle>

            <DialogContent dividers>
                {sent ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Email sent successfully to <strong>{recipientEmail}</strong>.
                    </Alert>
                ) : (
                    <>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {step === 'pick' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : templates.length === 0 ? (
                                    <Alert severity="info">
                                        No templates are linked to the <strong>{module}</strong> module yet.
                                        Go to <em>Settings &gt; Communications</em> and set a template's Linked Module to <strong>{module}</strong>.
                                    </Alert>
                                ) : (
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel shrink>Select Template</InputLabel>
                                        <NativeSelect
                                            value={selectedTemplateId}
                                            onChange={(e) => { setSelectedTemplateId(e.target.value); setError(null); }}
                                        >
                                            <option value="">— choose a template —</option>
                                            {templates.map(t => (
                                                <option key={t._id} value={t._id}>{t.name} — {t.subject}</option>
                                            ))}
                                        </NativeSelect>
                                        <FormHelperText>
                                            Only templates linked to the <strong>{module}</strong> module are shown.
                                        </FormHelperText>
                                    </FormControl>
                                )}

                                <TextField
                                    variant="standard"
                                    label="Recipient Email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    fullWidth
                                    type="email"
                                    helperText="You can change this before sending"
                                />
                            </Box>
                        )}

                        {step === 'preview' && preview && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Template: <strong>{preview.templateName}</strong>
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Subject: <strong>{preview.subject}</strong>
                                </Typography>

                                <TextField
                                    variant="standard"
                                    label="Recipient Email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    fullWidth
                                    type="email"
                                    sx={{ mb: 2 }}
                                    helperText="Review and edit before sending"
                                />

                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PreviewIcon fontSize="small" color="action" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Email Preview
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 2,
                                        maxHeight: 400,
                                        overflowY: 'auto',
                                        bgcolor: '#fafafa',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(preview.html)
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {sent ? (
                    <Button onClick={handleClose} variant="contained">
                        Close
                    </Button>
                ) : step === 'pick' ? (
                    <>
                        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                        <Button
                            onClick={handlePreview}
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={16} /> : <PreviewIcon />}
                            disabled={loading || !selectedTemplateId}
                        >
                            Preview
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setStep('pick')} disabled={loading}>Back</Button>
                        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                        <Button
                            onClick={handleSend}
                            variant="contained"
                            color="primary"
                            startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
                            disabled={loading || !recipientEmail}
                        >
                            Send Email
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
